import Vue from 'vue'
import router, { routes } from '@/router'
import dal from '@/data'
import { t } from '@/i18n'
import { rolesByCellType, roleFullNames } from '@/global'

const state = {
  limitDate: '2017-12-31',
  loadingCount: 0,
  drawer: false,
  snackbar: { visible: false, message: '' },
  dialog: {},
  cellType: { O: 'Open cell', L: 'Leaders cell' },
  maxWidth: '450px'
}

const getters = {
  drawerItems (state, getters, rootGetters) {
    return routes.filter(_ =>
      _.meta && _.meta.drawer && (_.meta.auth || false) === getters.authenticated &&
      (!_.meta.roles || getters.hasAnyRole(_.meta.roles)))
  },
  isLoading: state => state.loadingCount > 0,
  roles (state) {
    return function (cellType) {
      // const userRoleRank = roleRank.indexOf(getters.currentUser.role)
      const roleSet = rolesByCellType[cellType] || []
      return Object.keys(roleFullNames)
        .filter(key => roleSet.includes(key))
        .map(key => [key, t(roleFullNames[key])])
        .sort((a, b) => a[1] > b[1] ? 1 : a[1] < b[1] ? -1 : 0)
    }
  },
  roleName: state => key => t(roleFullNames[key]),
  hasKey: state => item => item && item['.key']
}

const mutations = {
  ...dal.bindMutations,
  config (state, payload) {
    const o = payload || {}
    Object.keys(o).forEach(k => Vue.set(state, k, o[k]))
  },
  error (state, e) {
    // Provide roleName to role
    if (e.params && e.params.role) e.params.roleName = getters.roleName(state)(e.params.role)
    state.dialog = {
      title: 'Error',
      content: e instanceof Error ? e.code ? t(e.message, e.params) : e.message : e,
      buttons: ['Close']
    }
  },
  loading (state, flag) {
    // Make sure it does not go below 0
    // state.loadingCount += flag ? 1 : state.loadingCount === 0 ? 0 : -1
    state.loadingCount += flag ? 1 : -1
  },
  title (state, payload) {
    state.title = payload
  },
  maxWidth (state, payload) {
    state.maxWidth = payload || '450px'
  },
  drawer (state, flag) {
    state.drawer = flag
  },
  snackbar (state, payload) {
    if (payload) {
      state.snackbar.visible = true
      state.snackbar.message = payload
    } else {
      state.snackbar.visible = false
    }
  },
  snackbarVisible(state, payload) {
    state.snackbar.visible = payload
  },

  dialog (state, payload) {
    switch (payload.type) {
      case 'delete':
        state.dialog = {
          title: 'Confirmation',
          content: t('Confirm delete #{param}', payload.params),
          buttons: ['No', 'Yes'],
          handler: action => {
            if (action === 'Yes') handle(payload, action)
          }
        }
        break
      case 'dirty':
        state.dialog = {
          title: 'Send the changes?',
          content: 'Dirty check message',
          buttons: ['Cancel', 'No', 'Yes'],
          handler: action => {
            switch (action) {
              case 'Yes':
                (payload.saver || payload.caller.save).call(payload.caller)
                handle(payload, action)
                break
              case 'No':
                // payload.caller.$store.commit('setDirty', false)
                handle(payload, action)
            }
          }
        }
        break
      default:
        state.dialog = payload
    }

    function handle (payload, action) {
      if (payload.handler) payload.handler.call(payload.caller, action)
    }
  }

}

const actions = {
  navigate (context, { path, next }) {
    return new Promise(function (resolve, reject) {
      router.push(path, onComplete, onAbort)

      function onComplete () {
        next && next()
        resolve()
      }

      function onAbort (reason) {
        // Who knows why router.push calls onAbort with no reason
        if (reason) {
          reject(reason)
        } else {
          resolve()
        }
      }
    })
  },

  async config (context, payload) {
    context.commit('config', await dal.config.findOne())
  },

  loading (context, payload) {
    context.commit('loading', payload)
  },
  load (context, promiseGetter) {
    context.commit('loading', true)
    return promiseGetter().then(value => {
      context.commit('loading', false)
      return value
    }, error => {
      context.commit('loading', false)
      throw error
    })
  },
  valid (context, payload) {
    const moduleState = context.state[payload.module]
    if (moduleState.dirty) {
      return new Promise((resolve, reject) => {
        // Because vuetify validation runs in nextTick
        Vue.nextTick(() => {
          if (moduleState.valid) {
            resolve(true)
          } else {
            context.commit('dialog', {
              title: 'Error',
              content: t('Fix errors', payload.params),
              buttons: ['OK'],
              handler: () => resolve(false)
            })
          }
        })
      })
    } else return Promise.resolve(true)
  },
  /** Id pyalod.dirty is true, user can asnwer:
     'Yes' - to save and pass
     'No' - to not save and pass
     'Cancel' - to not save and not pass
   Additionally when the user answear is 'Yes' it check valid status in nextTick
  */
  async dirty (context, payload) {
    if (payload.dirty) {
      return new Promise((resolve, reject) => {
        context.commit('dialog', {
          type: 'dirty',
          ...payload,
          handler: action => {
            switch (action) {
              case 'Yes':
                context.dispatch('valid', payload).then(v => resolve(v))
                break
              case 'No':
                context.commit(payload.module + '/dirty', false)
                resolve(true)
                break
              case 'Cancel':
                resolve(false)
            }
          }
        })
      })
    } else return true
  },
  async confirm (context, payload) {
    return new Promise((resolve, reject) => {
      context.commit('dialog', {
        title: 'Confirmation',
        content: t(payload.content, payload.params),
        buttons: ['No', 'Yes'],
        handler: action => resolve(action === 'Yes')
      })
    })
  },
  async confirmDelete (context, payload) {
    return new Promise((resolve, reject) => {
      context.commit('dialog', {
        type: 'delete',
        params: payload.params,
        handler: action => {
          resolve(action === 'Yes')
        }
      })
    })
  }
  // /** This is called by other actions to make sure the user is authenticated */
  // authenticate ({ dispatch, rootGetters }, callback) {
  //   return rootGetters.authenticated
  //     ? Promise.resolve(callback())
  //     : dispatch('navigate', { path: '/signin', callback })
  // }
}

export default { state, getters, mutations, actions }
